import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatApiErrorDetail, toImageSrc, avatarSrc } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreateReferralDialog } from "@/pages/ReferralsPage";
import { toast } from "sonner";
import { Envelope, Phone, Certificate, Briefcase, Lock, UserPlus, ArrowLeft, CheckCircle, Clock, ChatCircle } from "@phosphor-icons/react";

const HERO =
  "https://images.pexels.com/photos/13620067/pexels-photo-13620067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AgentProfilePage() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referProp, setReferProp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/agents/${id}`);
      setAgent(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const connect = async () => {
    try {
      await api.post(`/friends/request/${id}`);
      toast.success("Request sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading profile…</div>;
  }
  if (!agent) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Agent not found.</div>;
  }

  const isFriend = agent.friendship === "friends" || agent.friendship === "self";

  return (
    <div className="min-h-screen pb-24" data-testid="agent-profile-page">
      {/* Hero */}
      <div
        className="relative h-56 md:h-72 border-b hairline"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10,10,12,0.3), rgba(10,10,12,0.95)), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Link
          to="/network"
          className="absolute top-6 left-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-gold"
          data-testid="back-to-network"
        >
          <ArrowLeft size={14} /> Network
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-20">
        {/* Header */}
        <div className="card-surface rounded-xl p-6 md:p-8 relative">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar className="h-24 w-24 md:h-28 md:w-28 border-2 border-gold/40">
              <AvatarImage src={avatarSrc(agent.avatar_url)} />
              <AvatarFallback className="bg-ink-700 text-2xl">{agent.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{agent.specialty}</div>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-1">{agent.name}</h1>
              <div className="text-sm text-zinc-400 mt-1">{agent.area}{agent.city ? `, ${agent.city}` : ""}</div>

              <div className="flex flex-wrap gap-4 mt-4">
                <KeyStat label="Properties" value={agent.properties_count} />
                <KeyStat label="Closed" value={agent.sold_count} />
                <KeyStat label="Success Rate" value={`${agent.success_rate}%`} locked={!isFriend} />
              </div>
            </div>
            <div>
              {agent.friendship === "self" && (
                <Badge className="bg-white/5 border border-white/10 text-zinc-300">You</Badge>
              )}
              {agent.friendship === "friends" && (
                <div className="flex flex-col gap-2 items-end">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 gap-1.5">
                    <CheckCircle size={12} weight="fill" /> Connected
                  </Badge>
                  <Link
                    to={`/messages/${agent.id}`}
                    className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-md bg-gold text-ink-900 hover:bg-gold-light flex items-center gap-1.5"
                    data-testid="message-from-profile"
                  >
                    <ChatCircle size={12} weight="fill" /> Message
                  </Link>
                </div>
              )}
              {agent.friendship === "outgoing" && (
                <Badge className="bg-white/5 border border-white/10 text-zinc-300 gap-1.5">
                  <Clock size={12} /> Pending
                </Badge>
              )}
              {agent.friendship === "none" && (
                <Button onClick={connect} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="connect-from-profile">
                  <UserPlus size={14} weight="bold" className="mr-1.5" /> Connect
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {isFriend ? (
          <div className="grid md:grid-cols-3 gap-4 mt-6 stagger">
            <div className="md:col-span-2 card-surface rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Bio</div>
              <p className="text-zinc-300 leading-relaxed">{agent.bio || "—"}</p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <InfoRow icon={Envelope} label="Email" value={agent.email} />
                <InfoRow icon={Phone} label="Phone" value={agent.phone} />
                <InfoRow icon={Certificate} label="License" value={agent.license_no} />
                <InfoRow icon={Briefcase} label="Experience" value={`${agent.years_experience || 0} years`} />
              </div>
            </div>

            <div className="card-surface rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Recent inventory</div>
              <div className="space-y-3">
                {agent.properties?.slice(0, 6).map((p) => (
                  <div key={p.id} className="flex gap-3 items-center pb-3 border-b hairline last:border-0">
                    <div className="h-12 w-16 rounded bg-ink-700 bg-cover bg-center flex-shrink-0" style={{ backgroundImage: p.image_url ? `url(${toImageSrc(p.image_url)})` : undefined }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{p.title}</div>
                      <div className="text-xs text-zinc-500">{p.location}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-xs text-gold">{formatCurrency(p.price)}</div>
                      <button
                        type="button"
                        onClick={() => setReferProp(p)}
                        data-testid={`refer-client-btn-${p.id}`}
                        className="text-[10px] text-zinc-500 hover:text-gold mt-1 uppercase tracking-wider"
                      >
                        Refer my client →
                      </button>
                    </div>
                  </div>
                ))}
                {(!agent.properties || agent.properties.length === 0) && (
                  <div className="text-sm text-zinc-500">No listings available.</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="card-surface rounded-xl p-10 mt-6 text-center relative overflow-hidden" data-testid="locked-profile">
            <Lock size={28} className="text-gold mx-auto mb-3" />
            <div className="font-display text-2xl">This profile is private.</div>
            <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">
              Connect with {agent.name.split(" ")[0]} to view bio, contact details, inventory, and closing stats.
            </p>
            <Button onClick={connect} className="mt-6 bg-gold text-ink-900 hover:bg-gold-light" data-testid="connect-locked">
              <UserPlus size={14} weight="bold" className="mr-1.5" /> Send connection request
            </Button>
          </div>
        )}
      </div>
      {referProp && (
        <CreateReferralDialog
          open={!!referProp}
          onOpenChange={(o) => !o && setReferProp(null)}
          property={{ ...referProp, owner_id: agent?.id }}
        />
      )}
    </div>
  );
}

function KeyStat({ label, value, locked }) {
  return (
    <div>
      <div className={`font-mono text-xl ${locked ? "blur-[5px]" : "text-gold"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-gold mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="text-sm mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}
